/* Optional cart complements. No checkout interception, analytics, external services,
 * automatic additions, global product writes or changes to original cart handlers.
 */
(() => {
  'use strict';
  if (window.JoyCrossSell || customElements.get('joy-cross-sell') || !window.JoyCrossSellRules) return;
  let text;
  try { text = JSON.parse(document.getElementById('JoyCrossSellStrings')?.textContent || 'null'); }
  catch (_) { return; }
  if (!text?.heading || !text.reasons) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const signature = cart => JSON.stringify([cart?.currency, cart?.total_price, (cart?.items || []).map(i => [String(i.key || i.id), i.quantity, i.final_line_price])]);
  const state = {cart:null, instances:new Set(), cache:new Map(), busy:false, lastUpdate:0, read:null, notice:'', revision:0};
  const manager = window.JoyCrossSell = {
    version:'20261003-hyper150-2',
    update(cart) {
      if (!Array.isArray(cart?.items)) return;
      state.cart = cart; state.lastUpdate = Date.now(); state.revision += 1;
      state.instances.forEach(instance => instance.setCart(cart));
    },
    async readCart() {
      if (state.read) return state.read;
      const revision = state.revision;
      state.read = fetch(`${root()}cart.js`, {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}})
        .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
        .then(cart => { if (state.revision === revision) manager.update(cart); return cart; })
        .finally(() => { state.read = null; });
      return state.read;
    }
  };
  function reasonLabel(key) {
    const [kind, size] = key.split(':');
    const dictionary = text.reasons;
    return `${dictionary[kind] || ''}${size ? ` · ${size} mm` : ''}`;
  }
  function isInCart(handle, cart) { return (cart?.items || []).some(item => item.handle === handle); }
  async function productCard(handle, signal) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    const key = `${root()}|${window.Shopify?.currency?.active || ''}|${handle}`;
    const cached = state.cache.get(key);
    if (cached && Date.now() - cached.time < 15000) return cached.node?.cloneNode(true) || null;
    const url = new URL(`${root()}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', 'joy-cross-sell-product');
    const response = await fetch(url, {signal, credentials:'same-origin', cache:'no-store'});
    if (!response.ok) return null;
    const html = await response.text();
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-joy-product]');
    if (node && node.dataset.joyProduct !== handle) return null;
    state.cache.set(key, {time:Date.now(), node});
    if (state.cache.size > 60) state.cache.delete(state.cache.keys().next().value);
    return node?.cloneNode(true) || null;
  }
  class JoyCartComplements extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true; this.sequence = 0; this.active = false; this.loadingSignature = null; this.loadedSignature = null;
      this.panel = this.querySelector('[data-panel]'); this.list = this.querySelector('[data-list]');
      if (!this.panel || !this.list) return;
      this.querySelector('[data-heading]').textContent = text.heading;
      this.querySelector('[data-intro]').textContent = text.intro;
      this.querySelector('[data-dismiss]').setAttribute('aria-label', text.dismiss);
      this.querySelector('[data-status]').textContent = state.notice;
      try { if (!state.cart) state.cart = JSON.parse(this.querySelector('[data-cart]')?.textContent || 'null'); } catch (_) { /* Optional component must not interrupt checkout. */ }
      state.instances.add(this);
      this.submitHandler = event => { if (event.target.matches('[data-add-form]')) this.add(event); };
      this.dismissHandler = () => { this.dismissed = signature(this.cart); this.panel.hidden = true; };
      this.addEventListener('submit', this.submitHandler);
      this.querySelector('[data-dismiss]').addEventListener('click', this.dismissHandler);
      this.cart = state.cart;
      this.observer = new IntersectionObserver(entries => {
        this.active = entries.some(entry => entry.isIntersecting);
        if (this.active) this.load();
      }, {rootMargin:'150px 0px', threshold:0});
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.connected = false; this.sequence += 1; this.abort?.abort(); this.observer?.disconnect();
      state.instances.delete(this);
      this.removeEventListener('submit', this.submitHandler);
      this.querySelector('[data-dismiss]')?.removeEventListener('click', this.dismissHandler);
    }
    setCart(cart) {
      this.cart = cart;
      if (!cart.items.length) { this.sequence += 1; this.abort?.abort(); this.panel.hidden = true; this.list.replaceChildren(); this.loadedSignature = null; this.loadingSignature = null; return; }
      // Hide immediately any product that has just been added, including via other cart controls.
      this.list.querySelectorAll('[data-joy-product]').forEach(node => { if (isInCart(node.dataset.joyProduct, cart)) node.remove(); });
      if (!this.list.children.length) this.panel.hidden = true;
      if (this.active) this.load();
    }
    async load() {
      if (!this.cart?.items?.length || this.dismissed === signature(this.cart)) return;
      const sig = signature(this.cart);
      if (this.loadedSignature === sig || this.loadingSignature === sig) return;
      this.abort?.abort(); this.abort = new AbortController();
      const signal = this.abort.signal, generation = ++this.sequence;
      this.loadingSignature = sig;
      const groups = window.JoyCrossSellRules.plan(this.cart);
      const selected = [], used = new Set(); let requests = 0;
      // Two ranked groups at a time: bounded requests and predictable order, never a whole-catalog fetch.
      try {
        for (let start = 0; start < groups.length && selected.length < 3 && requests < 9; start += 2) {
          const rows = await Promise.all(groups.slice(start, start + 2).map(async group => {
            for (const target of group.targets.slice(0, 3)) {
              if (requests >= 9 || used.has(target.handle) || isInCart(target.handle, this.cart)) continue;
              requests += 1;
              const node = await productCard(target.handle, signal);
              if (node) return {node, group, target};
            }
            return null;
          }));
          for (const row of rows) {
            if (!row || selected.length >= 3 || used.has(row.target.handle)) continue;
            used.add(row.target.handle);
            row.node.querySelector('[data-reason]').textContent = reasonLabel(row.target.reason);
            row.node.querySelector('[data-source]').textContent = `${text.source}: ${row.group.source}`;
            row.node.querySelector('[data-source]').title = row.group.source;
            selected.push(row.node);
          }
        }
        if (!this.isConnected || generation !== this.sequence || signal.aborted) return;
        this.list.replaceChildren(...selected);
        this.list.querySelectorAll('[data-add-form] button').forEach(button => { button.disabled = state.busy; });
        this.panel.hidden = !selected.length || this.dismissed === sig;
        this.loadedSignature = sig;
      } catch (error) {
        if (error.name !== 'AbortError' && generation === this.sequence) { this.panel.hidden = true; this.loadedSignature = null; }
      } finally { if (generation === this.sequence) this.loadingSignature = null; }
    }
    async add(event) {
      event.preventDefault();
      if (state.busy) return;
      const form = event.target, button = form.querySelector('button');
      const id = String(new FormData(form).get('id') || '');
      if (!/^\d+$/.test(id)) return;
      const handle = form.closest('[data-joy-product]')?.dataset.joyProduct;
      if (isInCart(handle, state.cart)) return;
      state.busy = true; state.revision += 1;
      document.querySelectorAll('joy-cross-sell [data-add-form] button').forEach(el => { el.disabled = true; });
      const previousText = button.textContent;
      button.textContent = text.loading;
      this.querySelector('[data-status]').textContent = '';
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {bubbles:true, detail:{sections}}));
      const sectionIds = [...new Set(sections)].slice(0, 5);
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', Accept:'application/json'},
          body:JSON.stringify({items:[{id, quantity:1}], sections:sectionIds, sections_url:location.pathname})
        });
        const result = await response.json();
        if (!response.ok || result.status >= 400) {
          this.querySelector('[data-status]').textContent = typeof result.description === 'string' ? result.description : text.unavailable;
          state.cache.clear();
          this.loadedSignature = null;
          this.load();
          return;
        }
        state.notice = text.added;
        const cartResponse = await fetch(`${root()}cart.js`, {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        const theme = window.FoxTheme;
        // Native cart handlers need every bundled section; otherwise use the ordinary cart page.
        const complete = sectionIds.length > 0 && sectionIds.every(section => typeof result.sections?.[section] === 'string');
        if (complete && theme?.pubsub?.publish && theme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
          cart.sections = result.sections;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {cart});
          manager.update(cart);
        } else {
          location.assign(`${root()}cart`);
        }
        // Native cart replacement may disconnect the clicked card; restore a useful focus target.
        requestAnimationFrame(() => {
          const scope = document.querySelector('cart-drawer:not([hidden])') || document.querySelector('main-cart');
          const next = scope?.querySelector('joy-cross-sell [data-add-form] button:not(:disabled), button[name="checkout"]');
          if (!button.isConnected) next?.focus({preventScroll:true});
        });
      } catch (_) {
        // Never retry a POST with an uncertain result: that could add a duplicate.
        state.notice = text.error;
        this.querySelector('[data-status]').textContent = text.error;
        const link = document.createElement('a'); link.href = `${root()}cart`; link.textContent = ` → ${text.cart}`;
        this.querySelector('[data-status]').append(link);
        manager.readCart().catch(() => {});
      } finally {
        state.busy = false;
        document.querySelectorAll('joy-cross-sell [data-add-form] button').forEach(el => { el.disabled = false; });
        if (button.isConnected) button.textContent = previousText;
      }
    }
  }
  customElements.define('joy-cross-sell', JoyCartComplements);
  document.addEventListener('cart:updated', event => manager.update(event.detail?.cart));
  document.addEventListener('product-ajax:added', () => { if (Date.now() - state.lastUpdate > 500) manager.readCart().catch(() => {}); });
  window.addEventListener('pageshow', event => { if (event.persisted) { state.cache.clear(); manager.readCart().catch(() => {}); } });
})();
